import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
  MatLegacyTable as MatTable,
  MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';

import * as day from 'dayjs';
import { FlexTimeActivityInstance, MinimalFlexTimePeriod } from 'libs/domain';
import { BehaviorSubject, combineLatest, from, lastValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { PeopleFacadeService, Person } from '@app/src/app/people';
import { FiltersFormService } from '@app/src/app/shared/components/filters-form';
import {
  SystemAlertCloseEvents,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@app/src/app/shared/components/system-alert-modal';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { withDirtyParams } from '@app/src/app/shared/services/filtering/utils/query-processing.utils';

import { PeopleSelectorService } from '@modules/people-selector';

import { ModalOverlayService, ModalOverlayServiceCloseEventType } from '@shared/components/modal-overlay';
import { PaginatorComponent } from '@shared/components/paginator';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { CheckinFlowService } from '@shared/services/checkin/checkin-flow.service';
import { FlexTimeActivityInstanceService, FlexTimePeriodService } from '@shared/services/flex-time';
import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';
import { MediaService } from '@shared/services/media';

import { TeacherToolsFlexTimeService } from '../../../tools/teacher-tools/services/tt-flex-time.service';
import { FlexTimeManagerMessages } from '../../constants';
import { FtmActivityTemplatesService } from '../ftm-activity-templates/services';
import { FtmPeriodsActivityEditComponent } from '../ftm-periods/components/ftm-periods-activity-edit/ftm-periods-activity-edit.component';
import { FtmPeriodsActivityEditModalData } from '../ftm-periods/types';
import {
  FlexActivitiesQueryParams,
  flexActivitiesQuerySchema,
  FTM_ACTIVITIES_DISPLAY_COLUMNS,
  FtmActivitiesMessages,
} from './constants';

type FtmInstancesClientFilters = {
  textValue: string;
  createdByHash: string | null;
};

@Component({
  selector: 'mg-ftm-activities',
  templateUrl: './ftm-activities.component.html',
  styleUrls: ['./ftm-activities.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryParamService, FlexTimePeriodService],
})
export class FtmActivitiesComponent implements AfterViewInit, OnDestroy {
  // Children

  @ViewChild(MatTable) matTable: MatTable<FlexTimeActivityInstance>;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  @ViewChild(PaginatorComponent)
  paginator: PaginatorComponent;

  // Constants

  public readonly FTM_MSG = FlexTimeManagerMessages;
  public readonly MSG = FtmActivitiesMessages;

  // General Observables

  private readonly _destroyedSubject = new ReplaySubject<void>(1);

  private readonly _showAlertSubject = new BehaviorSubject<boolean>(true);
  public readonly showAlert$ = this._showAlertSubject.asObservable();

  public panelOpenState = false;
  public readonly isMobileView$: Observable<boolean> = this.media.breakpoint$.pipe(
    takeUntil(this._destroyedSubject),
    map(breakpoint => {
      return ['xsmall', 'small'].includes(breakpoint);
    }),
  );

  private _activeSortColumnSubject = new BehaviorSubject<string>(null);
  public readonly activeSortColumn$ = this._activeSortColumnSubject.asObservable();

  // Search Filter

  public readonly searchControl = this._fb.control(
    this._queryService.getQueryParamSnapshot(this._route.snapshot, flexActivitiesQuerySchema.parse).title,
  );

  private _hasFiltersActive = false;
  private readonly _debouncedSearchValue$ = this.searchControl.valueChanges.pipe(
    takeUntil(this._destroyedSubject),
    debounceTime(300),
    shareReplay(1),
    distinctUntilChanged(),
    map(value => value.toLowerCase()),
  );

  public selfManager = this._flexPermissions.isFlexTimeSelfManager();
  public isAdmin = this._flexPermissions.isFlexTimeAdmin();

  // Table Data

  public readonly dataSource = new MatTableDataSource<FlexTimeActivityInstance>([]);

  private _instancesSubj = new BehaviorSubject<FlexTimeActivityInstance[]>([]);

  public readonly displayedColumns$ = this.media.breakpoint$.pipe(
    takeUntil(this._destroyedSubject),
    map(breakpoint => {
      const columns = [...FTM_ACTIVITIES_DISPLAY_COLUMNS];
      const displayedColumns = ['xsmall', 'small', 'medium'].includes(breakpoint) ? ['mobile'] : columns || [];
      return displayedColumns;
    }),
  );

  public queryParams$ = this._queryService.useQueryParams(flexActivitiesQuerySchema.parse);

  private _periods$ = this.queryParams$.pipe(
    takeUntil(this._destroyedSubject),
    withDirtyParams(),
    filter(({ dirty }) => dirty.start_date || dirty.end_date),
    switchMap(({ params }) =>
      from(this._flexPeriod.fetchAllMinimal(params.start_date, params.end_date)).pipe(
        tap(periods => {
          const initialPeriodId = params.period_id || this._flexPeriod.getCurrentlyInProgressPeriod(periods)?.id;
          this.setPeriodFilter(initialPeriodId, true);
        }),
        catchError(error => {
          return of([]); // Return an empty array on error
        }),
      ),
    ),
    shareReplay(1),
  );

  public periodOptions$ = this._periods$.pipe(map(periods => this._flexPeriod.formatPeriodsOptions(periods)));

  public readonly hasPeriods$ = this._periods$.pipe(map(periods => periods?.length > 0));

  public activePeriod$: Observable<MinimalFlexTimePeriod> = combineLatest([
    this._periods$,
    this.queryParams$.pipe(
      map(params => params.period_id),
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([periods, periodId]) => {
      return periods?.find(p => p.id === periodId);
    }),
  );

  private readonly _selectedPeople$ = this.queryParams$.pipe(
    withDirtyParams(),
    filter(({ dirty }) => dirty.person_hashes),
    switchMap(({ params }) => {
      const hashes = [...(params.person_hashes || [])];
      return this._peopleService.getPeopleDetails(hashes).pipe(catchError(() => of([])));
    }),
  );

  // Filters

  public filtersFormStructure$ = combineLatest([this.queryParams$, this._selectedPeople$]).pipe(
    takeUntil(this._destroyedSubject),
    map(([params, selectedPeople]) => {
      return this._filtersForm.create({
        ['person_hashes']: {
          label: 'Name, ID number or Email',
          type: 'people-search',
          multiple: true,
          value: selectedPeople,
        },
        ['title']: {
          label: 'Search by title',
          type: 'text',
          value: params.title || '',
        },
      });
    }),
  );

  private readonly _isLoadingInstancesSubj = new BehaviorSubject(false);
  public readonly isLoadingInstances$ = this._isLoadingInstancesSubj.asObservable();

  // Empty State

  get showEmptyState() {
    return this._hasFiltersActive ? !this.dataSource?.filteredData?.length : !this.dataSource?.data?.length;
  }

  // Component Constructor

  constructor(
    public media: MediaService,
    private _cdr: ChangeDetectorRef,
    private _fb: UntypedFormBuilder,
    private _ttFlexTimeService: TeacherToolsFlexTimeService,
    private _flexInstance: FlexTimeActivityInstanceService,
    private _modalOverlay: ModalOverlayService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _flexPermissions: FlexTimePermissionsService,
    private _checkinOverlayService: CheckinFlowService,
    private _peopleSelector: PeopleSelectorService,
    private _auth: AuthInfoService,
    private _queryService: QueryParamService<FlexActivitiesQueryParams>,
    private _flexPeriod: FlexTimePeriodService,
    private _route: ActivatedRoute,
    private _ftActivityInstances: FlexTimeActivityInstanceService,
    private _systemAlertModal: SystemAlertModalService,
    private _filtersForm: FiltersFormService,
    private _peopleService: PeopleFacadeService,
    private _flexAtService: FtmActivityTemplatesService,
  ) {
    this._ttFlexTimeService.activityAssignmentSuccess$
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(() => this._revalidateData());

    this._peopleSelector
      .eventWhen('after-closed')
      .pipe(takeUntil(this._destroyedSubject), debounceTime(800))
      .subscribe(data => {
        this._revalidateData();
      });

    this._debouncedSearchValue$.pipe(takeUntil(this._destroyedSubject)).subscribe(value => {
      this._queryService.updateQueryParams({
        title: value,
      });
    });
  }

  // Lifecycle Hooks

  ngOnInit(): void {
    this.queryParams$
      .pipe(
        takeUntil(this._destroyedSubject),
        withDirtyParams(),
        filter(({ dirty }) => dirty.title),
      )
      .subscribe(async ({ params }) => {
        // client side filtering only
        this._filterClientSide({
          textValue: params.title,
        });
      });

    this.queryParams$
      .pipe(
        takeUntil(this._destroyedSubject),
        withDirtyParams(),
        tap(() => {}),
        filter(
          ({ dirty }) =>
            dirty.person_hashes ||
            dirty.start_date ||
            dirty.end_date ||
            dirty.period_id ||
            dirty.updated ||
            dirty.show_mine,
        ),
        tap(params => {
          this._isLoadingInstancesSubj.next(true);
          if (params.params.show_mine === 'mine') {
            const hash = this._auth.authPerson.hash;
            params.params.person_hashes = [hash];
          }
        }),
        switchMap(({ params }) =>
          from(
            this._ftActivityInstances.fetchAll({
              periodId: params.period_id,
              startDate: params.start_date,
              endDate: params.end_date,
              personHashes: params.person_hashes,
            }),
          ).pipe(
            catchError(error => {
              return of([]);
            }),
            finalize(() => {
              this._isLoadingInstancesSubj.next(false);
            }),
          ),
        ),
      )
      .subscribe(activities => {
        this._instancesSubj.next(activities);
      });

    this._instancesSubj.pipe(takeUntil(this._destroyedSubject)).subscribe(activities => {
      this.dataSource.data = activities;
      this._cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator.matPaginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (data: FlexTimeActivityInstance, sortHeaderId: string): string => {
      if (sortHeaderId === 'firstName') {
        return data.flexTimeActivity?.createdByPerson?.firstName.toLocaleLowerCase();
      } else if (sortHeaderId === 'lastName') {
        return data.flexTimeActivity?.createdByPerson?.lastName.toLocaleLowerCase();
      } else if (sortHeaderId === 'title') {
        return data.flexTimeActivity?.name.toLocaleLowerCase();
      }
      return '';
    };
    this.dataSource.sort.active = 'lastName';
    this.dataSource.sort.direction = 'desc';
    this.dataSource.filterPredicate = this._tableFilter;
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  //  Public Methods

  public updateFilters({ title, person_hashes }) {
    this._queryService.updateQueryParams({
      title: title || '',
      person_hashes: person_hashes?.map(p => p.hash) || [],
    });
  }

  public onDismissAlert() {
    this._showAlertSubject.next(false);
  }

  public setShowMineFilter(value: 'all' | 'mine') {
    if (!value) return;
    this._queryService.updateQueryParams({
      show_mine: value,
    });
  }

  public setTitleFilter(value: string) {
    this._queryService.updateQueryParams({
      title: value,
    });
  }

  public trackById(index: number, item: FlexTimeActivityInstance) {
    return item ? item.id : index;
  }

  public selectActiveColumn(columnKey: string) {
    this._activeSortColumnSubject.next(columnKey);
  }

  public changePersonHashes(people: Partial<Person>[]) {
    this._queryService.updateQueryParams({
      person_hashes: people.map(p => p.hash),
    });
  }

  public setPeriodFilter(periodId: number, replaceUrl = false) {
    this._queryService.updateQueryParams(
      {
        period_id: periodId,
      },
      // we want to replace current nav state when we automatically set closest period
      // on date range change since it's not a user action
      { replaceUrl },
    );
  }

  public activityCheckIn(item: FlexTimeActivityInstance): void {
    const reason = this._flexInstance.mapFlexActivityInstanceToReason(item);
    this._checkinOverlayService.setReason(reason);

    const canOverride = this._getRegistrationOverride(item);
    const isblockedFromManageRegistration = !this._flexPermissions.canManageActivityRegistration(item);

    this._peopleSelector.open('FlexTime Activity', 'checkin', {
      title: item.flexTimeActivity.name,
      data: {
        flexTimeActivityId: item.id,
        checkinReasonId: reason.id,
        registered: item.registered,
        spaces: item.spaces,
        flexTimePeriodId: item.flexTimePeriodId,
        date: item.flexTimePeriod.date,
        endTime: item.flexTimePeriod.endTime,
        canOverride,
        isblockedFromManageRegistration,
        flexTimeActivityName: item.flexTimeActivity.name,
        showProfilePicture: item.flexTimeActivity.showStudentPhotoOnCheckin,
        registrationDeadline: item.flexTimePeriod?.teacherRegistrationDeadline?.format('YYYY-MM-DD HH:mm:ss'),
      },
      pages: {
        checkin: {
          secondaryActionLabel: this.media.isMobileView ? 'Tardy' : 'Check in tardy',
        },
      },
    });
  }

  public assignActivity(activityInstance: FlexTimeActivityInstance): void {
    const canOverride = this._getRegistrationOverride(activityInstance);
    this._ttFlexTimeService.openActivityPeopleSelector(activityInstance, canOverride);
  }

  public async addActivity(periodId: number) {
    const modalRef = this._modalOverlay.open<FtmPeriodsActivityEditModalData>(FtmPeriodsActivityEditComponent, {
      data: { id: periodId },
      disposeOnNavigation: false,
    });

    const response = await lastValueFrom(modalRef.afterClosed);

    if (response?.type === ModalOverlayServiceCloseEventType.CREATE) {
      this._revalidateData();
      this._systemAlertSnackBar.open({
        type: 'success',
        message: FtmActivitiesMessages.ADD_ACTIVITY_SUCCESS,
      });
    }
  }

  public async deleteActivityInstance(item: FlexTimeActivityInstance) {
    if (!this._flexPermissions.canEditActivityInstance(item)) return;

    const modalRef = await this._systemAlertModal.open({
      modalType: SystemAlertModalType.WARNING,
      heading: "Are you sure you want to delete this activity, this can't be undone!",
      closeBtn: 'Cancel',
      confirmActionBtn: 'Delete',
    });

    const response = await lastValueFrom(modalRef.afterClosed());

    if (response?.type === SystemAlertCloseEvents.CONFIRM) {
      try {
        await this._ftActivityInstances.delete(item.id);
        const filtered = this._instancesSubj.value.filter(i => i.id !== item.id);

        this._instancesSubj.next(filtered);
      } catch (e) {
        this._systemAlertSnackBar.error('There was an error deleting activity');
      }
    }
  }

  public isInPast(data: MinimalFlexTimePeriod): boolean {
    const date = day(data.date).format('MMM DD YYYY');
    const parsed = `${date} ${data.endTime}`;

    const periodDate = day(parsed, 'MMM DD YYYY HH:mm:ss');

    return periodDate.isBefore(day(), 'minute');
  }

  public rangeChanged(range) {
    this._queryService.updateQueryParams(
      {
        start_date: range.start,
        end_date: range.end,
      },
      { queryParamsHandling: '' },
    );
  }

  public clickViewQrCode(item: FlexTimeActivityInstance) {
    this._flexAtService.openQrViewer(item.flexTimeActivity).catch(error => {
      this._systemAlertSnackBar.error('There was an error generating QR code');
    });
  }

  // Private Methods

  private _getRegistrationOverride(item: FlexTimeActivityInstance): boolean {
    if (this._flexPermissions.isFlexTimeAdmin()) {
      return true;
    }

    const registrationDeadline = day(item.flexTimePeriod.teacherRegistrationDeadline);
    const isInPast = registrationDeadline.isSameOrBefore(day(), 'minute');

    return !isInPast && this._flexPermissions.canManageActivityRegistration(item);
  }

  private _tableFilter(period: FlexTimeActivityInstance, search: string) {
    const { textValue, createdByHash } = JSON.parse(search) as FtmInstancesClientFilters;
    const { flexTimeActivity } = period;

    const searchableByStringValues = [
      flexTimeActivity?.name?.toLowerCase(),
      flexTimeActivity?.createdByPerson?.name?.toLowerCase(),
      flexTimeActivity?.location?.toLowerCase(),
    ].filter(v => v);

    const textSearchFilter = textValue?.length ? searchableByStringValues.some(v => v.includes(textValue)) : true;

    return [textSearchFilter].every(v => v);
  }

  private _filterClientSide({ textValue }) {
    this._hasFiltersActive = textValue?.length > 0;
    const filters: FtmInstancesClientFilters = {
      textValue,
      createdByHash: null,
    };
    this.dataSource.filter = JSON.stringify(filters);
    this._cdr.markForCheck();
  }

  private _revalidateData() {
    const snapshot = this._queryService.getQueryParamSnapshot(this._route.snapshot, flexActivitiesQuerySchema.parse);

    // just reload the page current page to revalidate the data
    this._queryService.updateQueryParams(
      {
        ...snapshot,
        updated: Date.now(),
      },
      {
        replaceUrl: true,
      },
    );
  }
}
